import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { PeoplePhoto } from '../people/PeoplePhoto';
import '../people/people.css';
import { FaCheckCircle } from 'react-icons/fa';

const emptyForm = { firstName: '', lastName: '', name: '', email: '', phoneNumber: '', referralCode: '', message: '', subscribe: false };

export default function ContactForm() {
  const [data, setData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [submitError, setSubmitError] = useState('');
  const [receiptEmailSent, setReceiptEmailSent] = useState(true);
  const inFlight = useRef(false);
  const successPanel = useRef(null);
  const submitting = status === 'submitting';

  useEffect(() => {
    if (status === 'success') successPanel.current?.focus();
  }, [status]);

  function change(event) {
    const { name, value, type, checked } = event.target;
    setData(previous => ({ ...previous, [name]: type === 'checkbox' ? checked : value }));
  }

  async function submit(event) {
    event.preventDefault();
    if (inFlight.current) return;
    const validation = {};
    const required = ['firstName', 'lastName', 'email', 'phoneNumber', 'message'];
    required.push('name');
    required.forEach(field => {
      if (!data[field].trim()) validation[field] = 'Please fill out this field.';
    });
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) validation.email = 'Please provide a valid email.';
    setErrors(validation);
    setSubmitError('');
    if (Object.keys(validation).length) return;
    inFlight.current = true;
    setStatus('submitting');
    try {
      const response = await axios.post('https://blaketechnologiessite.onrender.com/contact', {
        contactType: 'business',
        data: { ...data }
      });
      setReceiptEmailSent(response.data.receiptEmailSent !== false);
      setData(emptyForm);
      setStatus('success');
    } catch (error) {
      const response = error.response?.data;
      setSubmitError((response?.errMsg || 'We could not confirm your submission. Please check your connection and try again.') + (response?.requestId ? ` Reference: ${response.requestId}` : ''));
      setStatus('error');
    } finally {
      inFlight.current = false;
    }
  }

  function field(name, label, type = 'text', fullWidth = false) {
    const attributes = {
      id: `contact-${name}`, name, value: data[name], onChange: change,
      'aria-invalid': Boolean(errors[name]),
      'aria-describedby': errors[name] ? `error-${name}` : undefined,
      className: `appearance-none bg-transparent border-b w-full text-gray-700 py-3 focus:outline-none focus:border-indigo-700 ${errors[name] ? 'border-red-500' : 'border-indigo-500'}`,
      maxLength: name === 'message' ? 5000 : 254
    };
    return <div className={fullWidth ? 'md:col-span-2' : ''}>
      <label className="block text-gray-700 text-sm font-semibold" htmlFor={attributes.id}>{label}</label>
      {name === 'message' ? <textarea {...attributes} rows={4} /> : <input {...attributes} type={type} />}
      {errors[name] && <p id={`error-${name}`} className="text-red-600 text-sm mt-1">{errors[name]}</p>}
    </div>;
  }

  return <section className="py-2 lg:py-24">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="contact-people-panel">
          <h1>Contact us</h1>
          <p>Every project starts with a conversation. Tell us about your business, your people, and the work you want to improve.</p>
          <PeoplePhoto name="conversation" caption="Let’s understand what matters to your business." eager />
        </div>
        <div className="p-5 lg:px-11 lg:pb-11">
          <h2 className="text-black text-center font-semibold text-3xl mb-3">Business Contact</h2>
          <p className="text-gray-600 text-center mb-8">Tell us how we can help. We’ll reach out within 1–2 business days.</p>
          {submitError && <p role="alert" className="mb-6 rounded-lg bg-red-50 p-4 text-red-700">{submitError}</p>}
          {status === 'success' ? <div ref={successPanel} tabIndex={-1} role="status" className="rounded-xl border border-green-200 bg-green-50 p-8 text-center">
            <FaCheckCircle aria-hidden="true" className="mx-auto mb-4 h-12 w-12 text-green-600" />
            <h3 className="text-2xl font-semibold text-green-800">We received your message!</h3>
            <p className="mt-3 text-green-800">Thank you for contacting Blake Technologies. We’ll reach out within 1–2 business days.</p>
            <p className="mt-3 text-sm text-gray-700">{receiptEmailSent ? 'A confirmation email is on its way. Please check your inbox and spam folder.' : 'Your message is saved, but we couldn’t send your confirmation email. You don’t need to submit the form again.'}</p>
            <button type="button" className="mt-6 font-semibold text-indigo-600 underline" onClick={() => setStatus('idle')}>Send another message</button>
          </div> : <form onSubmit={submit} aria-busy={submitting} noValidate>
            <fieldset disabled={submitting} className="min-w-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {field('firstName', 'First Name')}
                {field('lastName', 'Last Name')}
                {field('name', 'Business Name', 'text', true)}
                {field('email', 'Email Address', 'email')}
                {field('phoneNumber', 'Phone Number', 'tel')}
                {field('referralCode', 'Referral Code (optional)')}
                {field('message', 'Your Message', 'text', true)}
              </div>
              <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                <label className="text-gray-600 text-sm"><input type="checkbox" name="subscribe" checked={data.subscribe} onChange={change} className="mr-2" />Send me your newsletter!</label>
                <button type="submit" disabled={submitting} className="inline-flex items-center justify-center shadow bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white font-bold py-3 px-6 rounded-lg">
                  {submitting && <span aria-hidden="true" className="h-4 w-4 mr-2 rounded-full border-2 border-white border-t-transparent animate-spin" />}
                  {submitting ? 'Sending…' : 'Submit'}
                </button>
              </div>
            </fieldset>
          </form>}
        </div>
      </div>
    </div>
  </section>;
}
